import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, EyeSlash, Eye, ChatCircleText } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Stars } from "../../components/ClassReview";

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso || "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export default function GroupReviewsSection() {
  const [data, setData] = useState(null);
  const load = () => api.get("/group/reviews").then(({ data }) => setData(data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try { await api.patch(`/group/reviews/${r.id}`, { hidden: !r.hidden }); toast.success(r.hidden ? "Review is public again" : "Review hidden from your public page"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <p className="tracker text-zinc-500">Loading…</p>;
  const { summary, distribution, by_class, reviews } = data;
  const maxDist = Math.max(1, ...Object.values(distribution));

  return (
    <div className="space-y-8" data-testid="group-reviews-section">
      <div className="grid md:grid-cols-[220px_1fr] gap-4">
        <div className="card-flat p-6 text-center" data-testid="group-reviews-summary">
          <div className="font-display text-6xl leading-none">{summary.avg ?? "—"}</div>
          <Stars value={summary.avg || 0} size={16} className="mt-2 justify-center" />
          <div className="tracker text-[10px] text-zinc-500 mt-2">{summary.count} REVIEW{summary.count === 1 ? "" : "S"}</div>
          <div className={`tracker text-[9px] mt-3 ${data.public_visible ? "text-emerald-400" : "text-zinc-500"}`} data-testid="group-reviews-public-status">
            {data.public_visible ? "★ SHOWING ON DISCOVER & YOUR PAGE" : `SHOWN PUBLICLY AFTER ${data.public_min} REVIEWS`}
          </div>
        </div>
        <div className="card-flat p-6 space-y-2" data-testid="group-reviews-distribution">
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-3 text-xs">
              <span className="tracker text-[10px] w-6 text-zinc-400">{n}★</span>
              <div className="flex-1 h-2 bg-white/5"><div className="h-2 bg-amber-400" style={{ width: `${(distribution[n] / maxDist) * 100}%` }} /></div>
              <span className="w-6 text-right text-zinc-500">{distribution[n]}</span>
            </div>
          ))}
        </div>
      </div>

      {by_class.length > 0 && (
        <section className="space-y-2" data-testid="group-reviews-by-class">
          <h3 className="tracker text-zinc-500 text-[10px]">BY CLASS</h3>
          <div className="divide-y divide-white/5 border border-white/10">
            {by_class.map((c) => (
              <div key={c.class_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm truncate">{c.title}</div><div className="tracker text-[9px] text-zinc-600">{when(c.start)}</div></div>
                <div className="flex items-center gap-3 shrink-0"><Stars value={c.avg} /><span className="font-display text-lg">{c.avg}</span><span className="tracker text-[9px] text-zinc-500">{c.count}</span></div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="tracker text-zinc-500 text-[10px] flex items-center gap-2"><ChatCircleText size={12} /> LATEST REVIEWS</h3>
        {reviews.length === 0 ? (
          <div className="card-flat p-10 text-center" data-testid="group-reviews-empty">
            <Star size={28} weight="duotone" className="text-amber-400 mx-auto mb-3" />
            <p className="font-display text-xl">No reviews yet.</p>
            <p className="text-zinc-500 text-sm mt-1">Everyone who attends gets a "How was your class?" email a couple of hours after it ends.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5 border border-white/10" data-testid="group-reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className={`px-4 py-3 flex items-start justify-between gap-3 ${r.hidden ? "opacity-50" : ""}`} data-testid={`group-review-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Stars value={r.rating} /><span className="text-sm font-semibold">{r.display_name}</span><span className="tracker text-[9px] text-zinc-600">{when(r.created_at)}</span>{r.hidden && <span className="tracker text-[9px] text-amber-400">HIDDEN</span>}</div>
                  {r.comment && <p className="text-sm text-zinc-300 mt-1">“{r.comment}”</p>}
                  <div className="tracker text-[9px] text-zinc-600 mt-1">{r.class_title} · {r.email}</div>
                </div>
                <button onClick={() => toggle(r)} className="btn-ghost !py-1 !px-2 !text-[9px] shrink-0" data-testid={`group-review-toggle-${r.id}`}>
                  {r.hidden ? <><Eye size={10} /> Show</> : <><EyeSlash size={10} /> Hide</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
